<template>
  <div>
    <div class="main-content fe-account-page">
      <div class="container py-4 py-lg-5">
        <header class="fe-account-page__header">
          <h1 class="fe-account-page__title">My account</h1>
          <p class="fe-account-page__subtitle">
            Manage your profile, orders, and saved items in one place.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div class="col-lg-9 col-md-8">
            <section class="fe-account-hero">
              <div class="fe-account-hero__text">
                <p class="fe-account-hero__welcome">
                  Welcome back,
                  <strong>{{ user.name }}</strong>
                </p>
                <p class="fe-account-hero__hint">
                  Here’s a quick overview of your account. Use the menu to jump to orders,
                  addresses, or checkout.
                </p>
              </div>
              <div class="fe-account-hero__badge" aria-hidden="true">
                <i class="fas fa-user-check"></i>
              </div>
            </section>

            <div class="row g-3 mb-4">
              <div class="col-sm-4">
                <router-link to="/my-orders" class="fe-stat-card">
                  <span class="fe-stat-card__icon"><i class="fas fa-box-open"></i></span>
                  <span class="fe-stat-card__label">Orders</span>
                  <span class="fe-stat-card__value">{{ orderCount }}</span>
                  <span class="fe-stat-card__cta">View all <i class="fas fa-chevron-right"></i></span>
                </router-link>
              </div>
              <div class="col-sm-4">
                <router-link to="/wishlist" class="fe-stat-card">
                  <span class="fe-stat-card__icon"><i class="fas fa-heart"></i></span>
                  <span class="fe-stat-card__label">Wishlist</span>
                  <span class="fe-stat-card__value">{{ wishlistCount }}</span>
                  <span class="fe-stat-card__cta">Browse <i class="fas fa-chevron-right"></i></span>
                </router-link>
              </div>
              <div class="col-sm-4">
                <router-link to="/my-address" class="fe-stat-card">
                  <span class="fe-stat-card__icon"><i class="fas fa-map-marker-alt"></i></span>
                  <span class="fe-stat-card__label">Addresses</span>
                  <span class="fe-stat-card__value">{{ addressCount }}</span>
                  <span class="fe-stat-card__cta">Manage <i class="fas fa-chevron-right"></i></span>
                </router-link>
              </div>
            </div>

            <section class="fe-account-card">
              <div class="fe-account-card__head">
                <h2 class="fe-account-card__title">Account details</h2>
                <span class="fe-account-card__tag">Profile</span>
              </div>
              <p class="fe-profile-hint">
                Update your display name and login email. Use an email you can access — it’s used for
                orders and sign-in.
              </p>
              <form class="fe-profile-form" @submit.prevent="saveProfile">
                <div class="fe-field">
                  <label class="fe-field__label" for="acc-name">Name</label>
                  <input
                    id="acc-name"
                    v-model="profileForm.name"
                    type="text"
                    class="fe-field__input"
                    autocomplete="name"
                    required
                  />
                </div>
                <div class="fe-field">
                  <label class="fe-field__label" for="acc-email">Email (login)</label>
                  <input
                    id="acc-email"
                    v-model="profileForm.email"
                    type="email"
                    class="fe-field__input"
                    autocomplete="email"
                    required
                  />
                </div>
                <p v-if="profileError" class="fe-field__error">{{ profileError }}</p>
                <div class="fe-profile-actions">
                  <button type="submit" class="fe-btn-save" :disabled="profileSaving">
                    {{ profileSaving ? 'Saving…' : 'Save changes' }}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </div>
    </div>

    <cta-area></cta-area>
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'
import { persistUserAndRefresh } from '@/utils/persist-user.js'
import { useWishlistStore } from '@/stores/wishlistStore'

export default {
  name: 'MyAccountPage',
  components: {
    CtaArea,
    AccountSidebar,
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')) || { name: 'Guest', email: '' },
      profileForm: { name: '', email: '' },
      profileSaving: false,
      profileError: '',
      orderCount: 0,
      addressCount: 0,
    }
  },
  computed: {
    wishlistCount() {
      const store = useWishlistStore()
      const list = store.wishlists
      return Array.isArray(list) ? list.length : 0
    },
  },
  mounted() {
    this.syncProfileForm()
    if (this.user && this.user.id) {
      this.fetchOrderCount()
      this.fetchAddressCount()
    }
  },
  methods: {
    syncProfileForm() {
      const u = JSON.parse(localStorage.getItem('user')) || {}
      this.profileForm = {
        name: u.name || '',
        email: u.email || '',
      }
    },
    async fetchOrderCount() {
      try {
        const response = await this.$axios.get(`/orders/user/${this.user.id}`)
        const data = response.data
        this.orderCount = Array.isArray(data) ? data.length : 0
      } catch {
        this.orderCount = 0
      }
    },
    async fetchAddressCount() {
      try {
        const response = await this.$axios.get(`/get-user-address/${this.user.id}`)
        const data = response.data
        this.addressCount = Array.isArray(data) ? data.length : 0
      } catch {
        this.addressCount = 0
      }
    },
    async saveProfile() {
      this.profileError = ''
      if (!this.user || !this.user.id) {
        this.profileError = 'You must be logged in to update your profile.'
        return
      }
      this.profileSaving = true
      try {
        const { data } = await this.$axios.put(`/web-user/${this.user.id}`, {
          name: this.profileForm.name.trim(),
          email: this.profileForm.email.trim(),
        })
        if (data.user) {
          persistUserAndRefresh(data.user)
        }
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          (e.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' ')) ||
          'Could not save profile.'
        this.profileError = msg
      } finally {
        this.profileSaving = false
      }
    },
  },
}
</script>

<style scoped>
/* Inter on the page wrapper only. Do not use :deep(*) — it overrides Font Awesome’s
   font-family on <i class="fas"> and icons vanish. Children inherit Inter for text;
   icons keep "Font Awesome 6 Free" from global CSS. */
.fe-account-page {
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', sans-serif;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 120px);
  color: #1f2937;
}

.fe-profile-hint {
  margin: 0 0 1.35rem;
  font-size: 17px;
  color: #475569;
  line-height: 1.62;
  font-weight: 500;
  letter-spacing: 0.005em;
}

.fe-profile-form {
  max-width: 480px;
}

.fe-field {
  margin-bottom: 1.2rem;
}

.fe-field__label {
  display: block;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  margin-bottom: 0.5rem;
}

.fe-field__input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.9rem 1.05rem;
  font-size: 16px;
  font-weight: 500;
  font-family: inherit;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  letter-spacing: 0.005em;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.fe-field__input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.fe-field__input:focus {
  outline: none;
  border-color: var(--fe-accent, #fcbe00);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fe-accent, #fcbe00) 22%, transparent);
}

.fe-field__error {
  color: #b91c1c;
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 0.85rem;
}

.fe-profile-actions {
  margin-top: 0.75rem;
}

.fe-btn-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.85rem;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  background: var(--fe-accent, #fcbe00);
  color: #ffffff;
  box-shadow: 0 8px 22px color-mix(in srgb, var(--fe-accent, #fcbe00) 32%, transparent);
  transition: transform 0.18s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.fe-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.fe-btn-save:hover:not(:disabled) {
  background: var(--fe-accent-hover, var(--fe-accent, #e5ac00));
  transform: translateY(-1px);
  box-shadow: 0 12px 28px color-mix(in srgb, var(--fe-accent, #fcbe00) 38%, transparent);
}

.fe-account-page__header {
  text-align: center;
  margin-bottom: 2.25rem;
}

.fe-account-page__title {
  font-size: clamp(1.95rem, 4.2vw, 2.45rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0 0 0.6rem;
  line-height: 1.15;
}

.fe-account-page__subtitle {
  margin: 0 auto;
  max-width: 560px;
  font-size: 18px;
  color: #475569;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0.005em;
}

.fe-account-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.6rem 1.75rem;
  margin-bottom: 1.5rem;
  border-radius: 18px;
  background: linear-gradient(
    120deg,
    #fff 0%,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 11%, #fff) 50%,
    #fff 100%
  );
  border: 1px solid #e8ecf1;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.06);
}

.fe-account-hero__welcome {
  margin: 0 0 0.45rem;
  font-size: 20px;
  color: #475569;
  font-weight: 500;
  letter-spacing: 0.005em;
}

.fe-account-hero__welcome strong {
  color: var(--fe-secondary, var(--fe-accent, #0f172a));
  font-weight: 700;
  letter-spacing: 0.005em;
}

.fe-account-hero__hint {
  margin: 0;
  font-size: 16px;
  color: #64748b;
  line-height: 1.55;
  max-width: 38rem;
  font-weight: 500;
}

.fe-account-hero__badge {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ffffff;
  background: var(--fe-accent, #fcbe00);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--fe-accent, #fcbe00) 38%, transparent);
}

.fe-stat-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  height: 100%;
  padding: 1.25rem 1.35rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8ecf1;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.05);
  text-decoration: none;
  color: inherit;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.fe-stat-card:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--fe-accent, #fcbe00) 50%, #cbd5e1);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}

.fe-stat-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 16%, #fff);
  color: var(--fe-accent, #fcbe00);
  font-size: 1.05rem;
}

.fe-stat-card__label {
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
}

.fe-stat-card__value {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.15;
}

.fe-stat-card__cta {
  margin-top: 0.4rem;
  font-size: 15px;
  font-weight: 600;
  color: var(--fe-secondary, var(--fe-accent, #d4a000));
  letter-spacing: 0.01em;
}

.fe-stat-card__cta i {
  font-size: 0.7rem;
  margin-left: 4px;
}

.fe-account-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.06);
  padding: 1.65rem 1.75rem 1.85rem;
}

.fe-account-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eef2f6;
}

.fe-account-card__title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.fe-account-card__tag {
  font-size: 12.75px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding: 0.32rem 0.75rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 18%, #fff);
  color: var(--fe-secondary, var(--fe-accent, #5c4a00));
}

@media (max-width: 575.98px) {
  .fe-account-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .fe-account-card {
    padding: 1.25rem 1.25rem 1.4rem;
  }

  .fe-stat-card__value {
    font-size: 1.75rem;
  }
}
</style>